import { useLayoutEffect, useRef, useState } from "react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui";
import { ContextRing } from "@/components/ui/context-ring";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { ComposerModelMenu } from "./composer-model-menu";

export type ComposerDirection = "inline" | "integrated" | "rail";

export function ComposerRedesign({
  direction,
  value,
  onChange,
  onSend,
  testId,
  busy = false,
  disabled = false,
  onStop,
}: {
  direction: ComposerDirection;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  testId: string;
  busy?: boolean;
  disabled?: boolean;
  onStop: () => void;
}) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [expanded, setExpanded] = useState(false);
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const shell = input.closest("form");
    if (!shell) return;
    const resize = () => {
      const footer = shell.querySelector<HTMLElement>(".composer-footer");
      const buttons = footer
        ? Array.from(footer.children).filter(
            (child) => child.tagName === "BUTTON",
          )
        : [];
      const controlsWidth = buttons.reduce(
        (width, button) => width + button.getBoundingClientRect().width,
        0,
      );
      const compactTextWidth = Math.max(
        1,
        shell.clientWidth - controlsWidth - 20,
      );
      input.style.width =
        direction === "inline" ? `${compactTextWidth}px` : "100%";
      input.style.height = "0px";
      setExpanded(direction === "inline" && input.scrollHeight > 32);
      input.style.width = "100%";
      input.style.height = "0px";
      input.style.height = `${Math.min(input.scrollHeight, 168)}px`;
    };
    resize();
    const observed = [
      shell,
      ...Array.from(
        shell.querySelectorAll<HTMLElement>(".composer-footer > button"),
      ),
    ];
    const widths = () =>
      observed
        .map((element) => element.getBoundingClientRect().width)
        .join(",");
    let previousWidths = widths();
    const observer = new ResizeObserver(() => {
      const nextWidths = widths();
      if (nextWidths === previousWidths) return;
      previousWidths = nextWidths;
      resize();
    });
    observed.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [value, direction, expanded]);

  return (
    <form
      className={cn("composer-proposal", `composer-${direction}`)}
      data-expanded={expanded}
      data-testid={testId}
      onSubmit={(event) => {
        event.preventDefault();
        if (value.trim() && !busy && !disabled) onSend();
      }}
    >
      <div className="composer-writing">
        <textarea
          ref={inputRef}
          rows={1}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          placeholder="Ask anything…"
          aria-label="Message"
          className="composer-text"
          data-testid={`${testId}-text`}
          onKeyDown={(event) => {
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              if (value.trim() && !busy && !disabled) onSend();
            }
          }}
        />
      </div>
      <div className="composer-footer">
        <ComposerModelMenu testId={testId} disabled={disabled || busy} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              className="composer-control h-7 w-7 shrink-0 rounded-full text-secondary hover:bg-[var(--color-interactive-hover)] hover:text-primary"
              aria-label="Chat context: 36% used"
              data-testid={`${testId}-context-open`}
            >
              <ContextRing
                percent={36}
                size={16}
                data-testid={`${testId}-context-ring`}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="end"
            sideOffset={12}
            className="dark composer-menu w-48"
            data-testid={`${testId}-context-menu`}
          >
            <div className="px-3 py-2">
              <p className="text-xs text-primary">Chat context</p>
              <p className="mt-2 text-sm text-primary">36% used</p>
              <p className="mt-1 text-xs text-muted">
                Sample conversation usage
              </p>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex-1" />
        <Button
          type={busy ? "button" : "submit"}
          variant="ghost"
          size="icon"
          disabled={disabled || (!busy && !value.trim())}
          onClick={
            busy
              ? (event) => {
                  event.preventDefault();
                  onStop();
                }
              : undefined
          }
          className={cn(
            "composer-control h-8 w-8 shrink-0 shadow-none",
            value.trim() || busy
              ? "text-primary hover:text-secondary"
              : "text-muted hover:text-primary",
          )}
          aria-label={busy ? "Stop response" : "Send message"}
          data-testid={`${testId}-send`}
        >
          {busy ? (
            <Icons.Square className="h-3 w-3 fill-current" />
          ) : (
            <Icons.ArrowUp className="h-4 w-4" />
          )}
        </Button>
      </div>
    </form>
  );
}
